import { useState, useEffect } from 'react';
import { getAuth, sendSignInLinkToEmail } from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { searchClientByPhone } from '../firebase/clientService';
import ClientProfile from './ClientProfile';
import NewClientForm from './NewClientForm';
import LogoutButton from '../components/auth/LogoutButton';
import PromptModal from '../components/common/PromptModal';
import { useAuth } from '../context/AuthContext';

function CheckInScreen() {
  const [searchTerm, setSearchTerm] = useState('');
  const [client, setClient] = useState(null);
  const [isNewClient, setIsNewClient] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('checkin');
  const [allClients, setAllClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [clientsLoading, setClientsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [showLinkModal, setShowLinkModal] = useState(false);
  const { currentUser } = useAuth();

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false }), 3000);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setLoading(true);
    setSearched(true);
    const result = await searchClientByPhone(searchTerm);
    if (result) {
      setClient(result);
    } else {
      setClient(null);
    }
    setLoading(false);
  };

  const handleBack = () => {
    setClient(null);
    setSearchTerm('');
    setSearched(false);
    setActiveMainTab('checkin');
  };

  const loadAllClients = async () => {
    setClientsLoading(true);
    const snapshot = await getDocs(collection(db, 'clients'));
    const clients = snapshot.docs.map(doc => ({ clientId: doc.id, ...doc.data() }));
    const sorted = clients.sort((a, b) => (a.phoneNumber || '').localeCompare(b.phoneNumber || ''));
    setAllClients(sorted);
    setFilteredClients(sorted);
    setClientsLoading(false);
  };

  const filterClients = (term) => {
    setClientSearchTerm(term);
    if (!term.trim()) {
      setFilteredClients(allClients);
    } else {
      const lowerTerm = term.toLowerCase();
      const filtered = allClients.filter(c => 
        (c.name && c.name.toLowerCase().includes(lowerTerm)) ||
        (c.phoneNumber && c.phoneNumber.includes(lowerTerm))
      );
      setFilteredClients(filtered);
    }
  };

  const openSendLinkModal = () => {
    setShowLinkModal(true);
  };

  const handleSendLink = async (values) => {
    const { email, phone } = values;
    if (!email) {
      showNotification('Please enter an email address', 'error');
      return;
    }
    if (!phone || !phone.match(/^0[6-8][0-9]{8}$/)) {
      showNotification('Please enter a valid SA phone number (e.g., 0721234567)', 'error');
      return;
    }
    const auth = getAuth();
    const actionCodeSettings = {
      url: `${window.location.origin}/client-intake?phone=${phone}&email=${encodeURIComponent(email)}`,
      handleCodeInApp: true,
    };
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      window.localStorage.setItem('emailForSignIn', email);
      showNotification(`✓ Intake link sent to ${email} for phone ${phone}`, 'success');
      setShowLinkModal(false);
    } catch (error) {
      showNotification('Error sending link: ' + error.message, 'error');
    }
  };

  useEffect(() => {
    setActiveMainTab('checkin');
  }, []);

  if (isNewClient) {
    return (
      <NewClientForm 
        phoneNumber={searchTerm}
        onComplete={(newClient) => {
          setClient(newClient);
          setIsNewClient(false);
        }}
        onCancel={() => {
          setIsNewClient(false);
          setSearchTerm('');
          setSearched(false);
        }}
      />
    );
  }

  if (client) {
    return <ClientProfile client={client} onBack={handleBack} />;
  }

  return (
    <div className="min-h-screen bg-salon-bg flex flex-col">
      {/* Notification Toast */}
      {notification.show && (
        <div className={`fixed top-20 right-4 z-50 p-4 rounded-md shadow-lg ${
          notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white animate-pulse`}>
          {notification.message}
        </div>
      )}

      {/* Send Link Modal */}
      <PromptModal
        isOpen={showLinkModal}
        title="Send Intake Link"
        message="Enter client details to send a secure intake link."
        fields={[
          { name: 'email', label: 'Email Address', type: 'email', placeholder: 'client@example.com', autoFocus: true },
          { name: 'phone', label: 'Phone Number', type: 'tel', placeholder: '0721234567', hint: 'South African mobile number (10 digits, starts with 0, second digit 6-8)' }
        ]}
        onConfirm={handleSendLink}
        onCancel={() => setShowLinkModal(false)}
      />

      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/images/abbr-grey.png" alt="Muse & Mane" className="h-10" />
            <span className="font-semibold text-gray-800">Salon Manager</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={openSendLinkModal} className="px-3 py-1 bg-salon-button text-white rounded-md text-sm hover:bg-salon-button-hover">✉️ Send Intake Link</button>
            <span className="text-sm text-gray-600">{currentUser?.email}</span>
            <LogoutButton />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-5xl">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="border-b border-gray-200">
              <nav className="flex gap-2 px-6 pt-4">
                <button 
                  className={`px-4 py-2 rounded-t-lg font-medium transition ${activeMainTab === 'checkin' ? 'bg-salon-button text-white' : 'text-gray-900 hover:bg-gray-50'}`} 
                  onClick={() => setActiveMainTab('checkin')}
                >
                  Check‑In
                </button>
                <button 
                  className={`px-4 py-2 rounded-t-lg font-medium transition ${activeMainTab === 'clients' ? 'bg-salon-button text-white' : 'text-gray-900 hover:bg-gray-50'}`} 
                  onClick={() => { setActiveMainTab('clients'); loadAllClients(); }}
                >
                  All Clients
                </button>
              </nav>
            </div>

            <div className="p-6">
              {activeMainTab === 'checkin' && (
                <div className="flex flex-col md:flex-row gap-8 items-center justify-center min-h-[400px]">
                  <div className="flex-1 text-center">
                    <img src="/images/logo-grey.png" alt="Muse & Mane" className="w-48 md:w-64 mx-auto" />
                    <p className="text-gray-500 mt-2">Salon Management System</p>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Client Check‑In</h2>
                    <div>
                      <input
                        type="tel"
                        placeholder="Phone number (e.g., 0721234567)"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        className="w-full px-4 py-3 text-lg border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-salon-button"
                      />
                      <button onClick={handleSearch} disabled={loading} className="w-full mt-4 bg-salon-button text-white py-3 rounded-md hover:bg-salon-button-hover disabled:opacity-50 transition text-lg">
                        {loading ? 'Searching...' : 'Search'}
                      </button>
                    </div>
                    {searched && !loading && !client && (
                      <div className="mt-6 text-center">
                        <p className="text-gray-600 mb-3">No client found with "{searchTerm}"</p>
                        <button onClick={() => setIsNewClient(true)} className="text-salon-button hover:underline">Create new client</button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeMainTab === 'clients' && (
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                    <h2 className="text-xl font-bold">All Clients ({filteredClients.length})</h2>
                    <input
                      type="text"
                      placeholder="Search by name or phone..."
                      value={clientSearchTerm}
                      onChange={(e) => filterClients(e.target.value)}
                      className="px-4 py-2 border border-gray-300 rounded-md w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-salon-button"
                    />
                  </div>
                  {clientsLoading ? (
                    <p className="text-center py-8">Loading clients...</p>
                  ) : filteredClients.length === 0 ? (
                    <p className="text-center py-8 text-gray-500">No clients found</p>
                  ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                      {filteredClients.map(c => (
                        <div key={c.clientId} onClick={() => setClient(c)} className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition">
                          <div className="font-medium">{c.name || 'Unnamed'}</div>
                          <div className="text-sm text-gray-500">{c.phoneNumber}</div>
                          {c.email && <div className="text-xs text-gray-400">{c.email}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckInScreen;